/**
 * Hangman Board Component
 *
 * SVG hangman figure drawn progressively, word display with blanks,
 * alphabet keyboard with color-coded used letters, and category display.
 */

import React, { useCallback } from 'react';
import {
  HangmanBoardProps,
  ALPHABET,
  MAX_WRONG_GUESSES,
} from '../types';
import './HangmanBoard.css';

const HangmanBoard: React.FC<HangmanBoardProps> = ({
  gameState,
  onGuessLetter,
  disabled = false,
}) => {
  const { word, guessedLetters, correctLetters, wrongLetters, wrongGuessCount, category } = gameState;

  const handleLetterClick = useCallback(
    (letter: string) => {
      if (disabled || guessedLetters.includes(letter)) return;
      onGuessLetter(letter);
    },
    [disabled, guessedLetters, onGuessLetter]
  );

  const getLetterStatus = useCallback(
    (letter: string): 'unused' | 'correct' | 'wrong' => {
      if (correctLetters.includes(letter)) return 'correct';
      if (wrongLetters.includes(letter)) return 'wrong';
      return 'unused';
    },
    [correctLetters, wrongLetters]
  );

  // Build word display
  const wordDisplay = word.split('').map((letter) =>
    guessedLetters.includes(letter) ? letter : '_'
  );

  const categoryLabel = category === 'all' ? 'Mixed' : category.charAt(0).toUpperCase() + category.slice(1);

  return (
    <div className="hangman-board" role="region" aria-label="Hangman game board">
      {/* Category display */}
      <div className="hangman-board__category" aria-label={`Category: ${categoryLabel}`}>
        <span className="hangman-board__category-label">Category:</span>
        <span className="hangman-board__category-value">{categoryLabel}</span>
      </div>

      {/* SVG Hangman Figure */}
      <div className="hangman-board__figure" aria-label={`Hangman figure: ${wrongGuessCount} of ${MAX_WRONG_GUESSES} wrong guesses`}>
        <svg
          viewBox="0 0 200 220"
          className="hangman-board__svg"
          role="img"
          aria-label={`Hangman drawing with ${wrongGuessCount} body parts`}
        >
          {/* Gallows — always visible */}
          {/* Base */}
          <line x1="20" y1="200" x2="100" y2="200" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          {/* Vertical pole */}
          <line x1="40" y1="200" x2="40" y2="20" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          {/* Horizontal beam */}
          <line x1="40" y1="20" x2="130" y2="20" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          {/* Rope */}
          <line x1="130" y1="20" x2="130" y2="50" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          {/* Support brace */}
          <line x1="40" y1="50" x2="70" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />

          {/* Body parts — drawn progressively */}
          {/* 1. Head */}
          {wrongGuessCount >= 1 && (
            <circle
              cx="130"
              cy="65"
              r="15"
              fill="none"
              stroke="var(--hangman-body-color, #e74c3c)"
              strokeWidth="3"
              className="hangman-board__body-part hangman-board__body-part--head"
            />
          )}
          {/* 2. Body */}
          {wrongGuessCount >= 2 && (
            <line
              x1="130" y1="80" x2="130" y2="130"
              stroke="var(--hangman-body-color, #e74c3c)"
              strokeWidth="3"
              strokeLinecap="round"
              className="hangman-board__body-part hangman-board__body-part--body"
            />
          )}
          {/* 3. Left arm */}
          {wrongGuessCount >= 3 && (
            <line
              x1="130" y1="95" x2="105" y2="115"
              stroke="var(--hangman-body-color, #e74c3c)"
              strokeWidth="3"
              strokeLinecap="round"
              className="hangman-board__body-part hangman-board__body-part--left-arm"
            />
          )}
          {/* 4. Right arm */}
          {wrongGuessCount >= 4 && (
            <line
              x1="130" y1="95" x2="155" y2="115"
              stroke="var(--hangman-body-color, #e74c3c)"
              strokeWidth="3"
              strokeLinecap="round"
              className="hangman-board__body-part hangman-board__body-part--right-arm"
            />
          )}
          {/* 5. Left leg */}
          {wrongGuessCount >= 5 && (
            <line
              x1="130" y1="130" x2="105" y2="165"
              stroke="var(--hangman-body-color, #e74c3c)"
              strokeWidth="3"
              strokeLinecap="round"
              className="hangman-board__body-part hangman-board__body-part--left-leg"
            />
          )}
          {/* 6. Right leg */}
          {wrongGuessCount >= 6 && (
            <line
              x1="130" y1="130" x2="155" y2="165"
              stroke="var(--hangman-body-color, #e74c3c)"
              strokeWidth="3"
              strokeLinecap="round"
              className="hangman-board__body-part hangman-board__body-part--right-leg"
            />
          )}
        </svg>
      </div>

      {/* Wrong guess counter */}
      <div className="hangman-board__guess-counter" role="status" aria-live="polite">
        <span className="hangman-board__guess-label">Wrong guesses:</span>
        <span className={`hangman-board__guess-value ${wrongGuessCount >= 4 ? 'hangman-board__guess-value--danger' : ''}`}>
          {wrongGuessCount} / {MAX_WRONG_GUESSES}
        </span>
      </div>

      {/* Word display */}
      <div
        className="hangman-board__word"
        role="status"
        aria-live="polite"
        aria-label={`Word: ${wordDisplay.map((c) => (c === '_' ? 'blank' : c)).join(', ')}`}
      >
        {wordDisplay.map((char, idx) => (
          <span
            key={idx}
            className={`hangman-board__letter ${char === '_' ? 'hangman-board__letter--blank' : 'hangman-board__letter--revealed'}`}
          >
            {char}
          </span>
        ))}
      </div>

      {/* Alphabet keyboard */}
      <div className="hangman-board__keyboard" role="group" aria-label="Letter keyboard">
        {ALPHABET.map((letter) => {
          const status = getLetterStatus(letter);
          const isUsed = status !== 'unused';

          return (
            <button
              key={letter}
              className={`hangman-board__key hangman-board__key--${status}`}
              onClick={() => handleLetterClick(letter)}
              disabled={disabled || isUsed}
              aria-label={`${letter}${isUsed ? ` (${status})` : ''}`}
              aria-pressed={isUsed}
            >
              {letter}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { HangmanBoard };
export default HangmanBoard;
